import React from 'react';
import { faLayerGroup } from '@fortawesome/free-solid-svg-icons';
import BlogPost from '../BlogPost';
// Create: src/assets/images/blogs/reason-across-silos.jpg
import reasonAcrossSilosImg from '../../../assets/images/blogs/AI-Reasoning-Across-Silos.png';

const ReasonAcrossSilos = () => (
    <BlogPost
        title="Your Business Works in Silos. Your AI Should Reason Across Them."
        subtitle="We're trying to solve a reasoning problem with a modelling exercise. That's why it keeps failing."
        category="Strategy"
        categoryIcon={faLayerGroup}
        color="#003366"
        date="March 2026"
        readTime="5 min read"
        image={reasonAcrossSilosImg}
        imageAlt="AI reasoning across business silos - compliance, operations, legal, finance"
        ctaHeading="Ready to reason across silos?"
        ctaText="Book a discovery call and we'll show you how Nebulyx Cognition reasons across domains without forcing everyone into one schema."
        seoDescription="Why building a shared ontology fails and how AI that understands each domain in its own terms can reason across silos at query time."
        seoKeywords="business silos, cross-domain reasoning, AI semantics, compliance AI, Nebulyx Cognition, enterprise AI"
        slug="reason-across-silos"
    >
        <p>
            Your compliance team calls it an "obligation."<br />
            Your operations team calls it a "procedure."<br />
            Your legal team calls it a "requirement."
        </p>

        <p>
            All talking about the same thing.
        </p>

        <p>
            <strong>This is the knowledge problem all organisations grapple with.</strong>
        </p>

        <h3>The Real Issue</h3>

        <p>
            The issue isn't that the information doesn't exist. It's that every domain has
            encoded it in its own language, its own structure, its own context.
        </p>

        <p>
            And when you need to reason across those boundaries - "are we actually
            compliant?" - you're relying on someone who knows both worlds well enough
            to join the dots manually.
        </p>

        <p>
            <strong>That person is usually expensive, overworked &amp; about to retire.</strong>
        </p>

        <h3>In Regulated Industries</h3>

        <p>
            Compliance builds a register of obligations mapped to frameworks like MiFID or
            SOX. Operations writes procedures that implement those obligations. Legal
            interprets the regulation differently again, with caveats &amp; jurisdictional
            nuances. And finance tracks the cost of all of it using a completely different
            taxonomy.
        </p>

        <p>
            Four domains. Same underlying reality. But four different ways of describing
            their view on it. And those views are different - i.e. the semantics are not
            the same across them.
        </p>

        <h3>The Traditional Answer</h3>

        <p>
            Build an ontology, map everything to a common model &amp; get everyone to agree
            on the vocabulary.
        </p>

        <p>
            We all know what happens next.
        </p>

        <blockquote className="blog-post-quote">
            "Eighteen months of workshops. A beautiful model that's out of date before it's
            finished. And a business still asking: 'If this regulation changes tomorrow,
            what actually breaks?'"
        </blockquote>

        <p>
            This keeps failing because we're trying to solve a reasoning problem with a
            modelling exercise.
        </p>

        <h3>A Different Approach</h3>

        <p>
            What if instead of forcing every domain into one shared schema, you had an AI
            layer that understood each domain in its own terms &amp; reasoned across them
            at query time?
        </p>

        <p>
            <strong>That's what we've built with Nebulyx Cognition.</strong>
        </p>

        <p>
            Cognition doesn't just store triples. It encodes the actual semantics of
            language - the full meaning, not just subject-predicate-object.
        </p>

        <p>
            When compliance says "obligation" &amp; operations says "procedure" &amp; legal
            says "requirement," the model understands the relationship between them without
            anyone manually mapping it.
        </p>

        <p>
            You ingest the regulation, the operating procedures, the legal interpretations.
            Each stays in its own domain context with the reasoning layer sitting across
            all of them.
        </p>

        <h3>The Result</h3>

        <p>
            When you ask "what's my exposure if this regulation changes?" - it doesn't
            search one silo. It reasons across compliance, operations &amp; legal
            simultaneously.
        </p>

        <p>
            Full traceability back to source document, paragraph &amp; sentence.
        </p>

        <p>
            No six-month alignment project. No master ontology that breaks when someone
            adds a new framework. No reliance on the one person who holds it all in their
            head.
        </p>

        <p>
            The knowledge already exists in your organisation - policies, standards,
            contracts, procedures &amp; emails.
        </p>

        <p>
            <strong>The problem was never capturing it or even modelling it. The problem
            was reasoning across it.</strong> 🎯
        </p>
    </BlogPost>
);

export default ReasonAcrossSilos;
